import { motion } from 'framer-motion'
import React, { useMemo, useState } from 'react'
import { useModal } from '../../../components/useModal'
import AdminAddAgency from '../../../forms/Admin/AdminAddAgency'
import { ReactComponent as ActionDelete } from '../../../images/icons/action_delete.svg'
import { ReactComponent as ActionEdit } from '../../../images/icons/action_edit.svg'
import AdminEditAgency from '../../../forms/Admin/AdminEditAgency'
import AdminDeleteAgency from '../../../forms/Admin/AdminDeleteAgency'
import * as portals from 'react-reverse-portal'
import { createColumnHelper } from '@tanstack/react-table'
import { Organization } from '../../../lib/types/Organization'
import { formatNumber } from '../../../lib/utils/formatNumber'
import FiltersContext from '../../../components/Filters/FiltersContext'
import { useGetOrganizations } from '../../../api/admin/useGetOrganizations'
import { TextInput, Button, Table } from '@demandlocal/components'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const columnHelper = createColumnHelper<Organization>()

const AdminAgencies = (): JSX.Element => {
  const [_page, setPage] = useState(0)
  const [filters, setFilters] = useState<Partial<Organization>>({})
  const filtersPortalNode = useMemo(() => portals.createHtmlPortalNode(), [])
  const { handleModal } = useModal()
  const { t } = useTranslation('routes')

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: () => 'ID',
      }),
      columnHelper.accessor('name', {
        header: () => 'Name',
        cell: ({ row }) => (
          <Link
            to={t('agency.profile.overview', { agency_id: row.original.id })}
            className="text-primary-persian-blue font-bold">
            {row.original.name}
          </Link>
        ),
      }),
      columnHelper.accessor('dealership_count', {
        header: () => 'Dealerships',
        cell: (info) => formatNumber(info?.getValue()),
      }),
      columnHelper.accessor('user_count', {
        header: () => 'Users',
        cell: (info) => formatNumber(info?.getValue()),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => 'Actions',
        cell: (info) => (
          <div className={'flex flex-row'}>
            <ActionDelete
              className={'cursor-pointer'}
              onClick={() =>
                handleModal(<AdminDeleteAgency agency={info.row.original} />)
              }
            />
            <ActionEdit
              stroke={'#0932C2'}
              className={'ml-4 cursor-pointer'}
              onClick={() => {
                handleModal(<AdminEditAgency agency={info.row.original} />)
              }}
            />
          </div>
        ),
      }),
    ],
    [handleModal, t],
  )

  return (
    <motion.div className={'flex flex-1 flex-col pb-8 font-Spartan'}>
      <div className={'flex flex-1 flex-col'}>
        <div className={'pb-4 flex justify-between'}>
          <div
            className={
              'font-Spartan font-bold text-white rounded-md text-blue-primary text-base'
            }>
            Agencies
          </div>
          <div className={'flex gap-4'}>
            <portals.OutPortal node={filtersPortalNode} />
            <Button onClick={() => handleModal(<AdminAddAgency />)}>
              + Add new agency
            </Button>
          </div>
        </div>
        <FiltersContext portalNode={filtersPortalNode} setFilters={setFilters}>
          <TextInput name={'organization_name'} placeholder={'Name'} label={'Name'} />
        </FiltersContext>
        <Table columns={columns} filters={filters} fetchFn={useGetOrganizations} />
      </div>
    </motion.div>
  )
}

export default AdminAgencies
